import type { BookingQuestionItem, BookingQuestionKind } from '../../../api/comms';

/**
 * Clarifying questions as the box renders them: every question resolved to a
 * concrete input kind. The service's typed `questionItems` win when present;
 * bare strings (an older service, a model that ignored the schema) fall back
 * to a conservative wording heuristic so a date still gets a date picker.
 */

export interface UiQuestion {
  text: string;
  kind: BookingQuestionKind;
  /** Non-empty only for `choice`, and then always at least two entries. */
  options: string[];
}

const KINDS: ReadonlySet<string> = new Set(['text', 'choice', 'date', 'time', 'datetime', 'number']);

/**
 * Wording fallback. Deliberately conservative: only explicit date/time/count
 * questions get a special control — a wrong picker is worse than a text box.
 */
export const inferQuestionKind = (text: string): BookingQuestionKind => {
  const t = text.toLowerCase();
  const date = /\b(which|what)\s+(date|day)\b/.test(t) || /\bdate\b/.test(t);
  const time = /\b(which|what)\s+time\b/.test(t) || /\btime\b/.test(t);
  if (date && time) return 'datetime';
  if (time) return 'time';
  if (date) return 'date';
  if (/\bhow many\b/.test(t)) return 'number';
  return 'text';
};

const sanitize = (item: BookingQuestionItem): UiQuestion | null => {
  const text = String(item.text ?? '').trim();
  if (!text) return null;
  const options = Array.isArray(item.options)
    ? [...new Set(item.options.map((o) => String(o).trim()).filter(Boolean))]
    : [];
  // Two real options force choice whatever the declared kind; fewer than two
  // can never render as a menu, so the question degrades to the heuristic.
  if (options.length >= 2) return { text, kind: 'choice', options };
  const declared = item.kind && KINDS.has(item.kind) ? item.kind : null;
  const kind = declared && declared !== 'choice' ? declared : inferQuestionKind(text);
  return { text, kind: kind === 'choice' ? 'text' : kind, options: [] };
};

export function resolveQuestions(
  questions: string[],
  items?: BookingQuestionItem[],
): UiQuestion[] {
  const source: BookingQuestionItem[] = items?.length
    ? items
    : questions.map((text) => ({ text }));
  return source.map(sanitize).filter((q): q is UiQuestion => q !== null);
}
